import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { QRCodeSVG } from 'qrcode.react';
import { Printer } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LabelPrintDialog({ open, onOpenChange, deliveryNote, token, isOrder = false }) {
  const [labelSize, setLabelSize] = useState('small');
  const [qrData, setQrData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [companyName, setCompanyName] = useState('');

  useEffect(() => {
    if (open && deliveryNote && token) {
      setQrData(null);
      setCustomerName('');
      setCompanyName('');
      fetchQRCode();
      fetchCustomerOrSupplier();
      fetchCompanyName();
    }
  }, [open, deliveryNote, token]);

  const fetchQRCode = async () => {
    setIsLoading(true);
    try {
      const endpoint = isOrder 
        ? `${API}/orders/${deliveryNote.id}/qr-code`
        : `${API}/delivery-notes/${deliveryNote.id}/qr-code`;
      const response = await axios.get(
        endpoint,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQrData(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden des QR-Codes');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCustomerOrSupplier = async () => {
    try {
      if (isOrder && deliveryNote.supplier_id) {
        const response = await axios.get(`${API}/suppliers/${deliveryNote.supplier_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCustomerName(response.data.company_name || '');
      } else if (!isOrder && deliveryNote.customer_id) {
        const response = await axios.get(`${API}/customers/${deliveryNote.customer_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCustomerName(response.data.company_name || '');
      }
    } catch (error) {
      console.error('Fehler beim Laden:', error);
    }
  };

  const fetchCompanyName = async () => {
    try {
      const response = await axios.get(`${API}/settings/company`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanyName(response.data.company_name || 'Metaxon');
    } catch (error) {
      console.error('Fehler beim Laden des Firmennamens:', error);
      setCompanyName('Metaxon'); // Fallback
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '', 'width=800,height=600');
    const content = document.getElementById('label-print-content').innerHTML;
    
    const size = labelSizes[labelSize];
    const isSmall = labelSize === 'small';
    
    if (isSmall) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Etikett Drucken</title>
            <style>
              @page {
                size: ${size.width} ${size.height} landscape;
                margin: 0;
              }
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }
              body {
                margin: 0;
                padding: 0;
                font-family: 'Segoe UI', Arial, sans-serif;
                background: white;
              }
              #label-content {
                width: ${size.width};
                height: ${size.height};
                background: white;
                position: relative;
              }
              .label-header {
                position: absolute;
                left: 3mm;              /* 0mm + 3mm = 3mm (Text nach rechts) */
                top: ${size.headerTop};
                width: 76.87mm;
                height: ${size.headerHeight};
                text-align: center;
                font-size: ${size.headerSize};
                font-weight: bold;
                display: flex;
                align-items: center;
                justify-content: center;
              }
              .label-field {
                position: absolute;
                left: 3.16mm;           /* 0.16mm + 3mm = 3.16mm (Text nach rechts) */
                font-size: ${size.labelSize};
              }
              .field-label {
                font-weight: bold;
              }
              .field-value {
                font-weight: normal;
              }
              .kunde-label { top: ${size.kundeTop}; }
              .kunde-value { top: ${size.kundeValueTop}; width: 54.26mm; }
              .ref-label { top: ${size.refTop}; }
              .ref-value { top: ${size.refValueTop}; width: 45.52mm; }
              .projekt-label { top: ${size.projektTop}; }
              .projekt-value { top: ${size.projektValueTop}; width: 40.77mm; }
              .qr-code {
                position: absolute;
                left: ${size.qrLeft};
                top: ${size.qrTop};
                width: ${size.qrSize};
                height: ${size.qrSize};
              }
              .qr-code svg {
                width: 100%;
                height: 100%;
                display: block;
              }
              .qr-scan-text {
                position: absolute;
                left: ${size.scanTextLeft};
                top: ${size.scanTextTop};
                width: 6.88mm;
                height: 28.39mm;
                font-size: ${size.scanTextSize};
                writing-mode: vertical-rl;
                transform: rotate(180deg);
                display: flex;
                align-items: center;
                justify-content: center;
                text-align: center;
              }
            </style>
          </head>
          <body>
            ${content}
          </body>
        </html>
      `);
    } else {
      // Large label with absolute positioning
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Etikett Drucken</title>
            <style>
              @page {
                size: ${size.width} ${size.height} landscape;
                margin: 0;
              }
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }
              body {
                margin: 0;
                padding: 0;
                font-family: Arial, sans-serif;
                background: white;
              }
              #label-content {
                width: ${size.width};
                height: ${size.height};
                background: white;
                padding: ${size.padding};
                position: relative;
              }
              .label-header {
                position: absolute;
                text-align: center;
                font-size: ${size.headerSize};
                font-weight: bold;
                width: 100%;
                top: ${size.headerTop || '0mm'};
                left: 0mm;
              }
              .label-field {
                position: absolute;
                left: ${size.textLeft || '0mm'};
                font-size: ${size.labelSize};
              }
              .field-label {
                font-weight: bold;
              }
              .field-value {
                font-weight: normal;
                font-size: ${size.valueSize};
              }
              .kunde-label { top: ${size.kundeTop}; }
              .kunde-value { top: ${size.kundeValueTop}; }
              .ref-label { top: ${size.refTop}; }
              .ref-value { top: ${size.refValueTop}; }
              .projekt-label { top: ${size.projektTop}; }
              .projekt-value { top: ${size.projektValueTop}; }
              .qr-code {
                position: absolute;
                left: ${size.qrLeft};
                top: ${size.qrTop};
                width: ${size.qrSize};
                height: ${size.qrSize};
              }
              .qr-code svg {
                width: 100%;
                height: 100%;
                display: block;
              }
              .qr-scan-text {
                position: absolute;
                left: ${size.scanTextLeft};
                top: ${size.scanTextTop};
                width: ${size.qrSize};
                font-size: ${size.scanTextSize};
                text-align: center;
              }
            </style>
          </head>
          <body>
            ${content}
          </body>
        </html>
      `);
    }
    
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const labelSizes = {
    small: {
      width: '89mm',
      height: '41mm',
      headerTop: '5.5mm',        // 7.5mm - 2mm = 5.5mm (oberste Reihe nach oben)
      headerSize: '10pt',
      headerHeight: '6mm',
      kundeTop: '14mm',          
      kundeSize: '9pt',
      kundeValueTop: '17mm',     
      refTop: '22mm',            
      refValueTop: '25mm',       
      projektTop: '30mm',        
      projektValueTop: '33mm',   
      labelSize: '9pt',
      valueSize: '9pt',
      qrLeft: '48mm',            // 51mm - 3mm = 48mm (QR-Code nach links)
      qrTop: '12mm',             
      qrSize: '28mm',
      scanTextLeft: '41mm',      // 44mm - 3mm = 41mm (Scan-Text auch nach links)
      scanTextTop: '13mm',       
      scanTextSize: '5pt',
      scanTextRotation: 270,
      layout: 'compact'
    },
    large: {
      width: '152mm',
      height: '102mm',
      padding: '5mm',
      headerSize: '20pt',
      headerTop: '10mm',         
      textLeft: '8mm',           // Linke Spalte 8mm nach rechts
      kundeTop: '26mm',          
      kundeValueTop: '31mm',     
      refTop: '41mm',            
      refValueTop: '46mm',       
      projektTop: '56mm',        
      projektValueTop: '61mm',   
      labelSize: '16pt',
      valueSize: '16pt',
      qrLeft: '91mm',            
      qrTop: '26mm',            
      qrSize: '60mm',
      scanTextLeft: '91mm',      
      scanTextTop: '87mm',      
      scanTextSize: '14pt',
      layout: 'spacious'
    }
  };

  const currentSize = labelSizes[labelSize];
  const deliveryNoteNumber = qrData?.delivery_note_number || deliveryNote?.project_number || 'N/A';
  
  // Use order_number from delivery note (e.g., "Bestellung 830975")
  const referenceNumber = deliveryNote?.order_number || 'N/A';

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>🖨️ Etikett drucken</DialogTitle>
            <DialogDescription>
              Wählen Sie die Etikettengröße und drucken Sie das QR-Code-Etikett
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Size Selection */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Etikettengröße auswählen</Label>
              <RadioGroup value={labelSize} onValueChange={setLabelSize}>
                <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-gray-50">
                  <RadioGroupItem value="small" id="small" />
                  <Label htmlFor="small" className="flex-1 cursor-pointer">
                    <div className="font-medium">Klein - 89mm x 41mm</div>
                    <div className="text-sm text-gray-500">DYMO 11356 / SS0722560</div>
                  </Label>
                </div>
                <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-gray-50">
                  <RadioGroupItem value="large" id="large" />
                  <Label htmlFor="large" className="flex-1 cursor-pointer">
                    <div className="font-medium">Groß - 4" x 6" (152mm x 102mm)</div>
                    <div className="text-sm text-gray-500">DYMO LabelWriter LW 5XL</div>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Preview */}
            {isLoading ? (
              <div className="space-y-3">
                <Label className="text-base font-semibold">Vorschau</Label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 bg-gray-50 flex justify-center items-center min-h-[200px]">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">QR-Code und Daten werden geladen...</p>
                  </div>
                </div>
              </div>
            ) : qrData && customerName ? (
              <div className="space-y-3">
                <Label className="text-base font-semibold">Vorschau</Label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 bg-gray-50 flex justify-center overflow-auto">
                  <div 
                    className="bg-white border-2 border-gray-400 shadow-lg"
                    style={{
                      width: currentSize.width,
                      height: currentSize.height,
                      minWidth: currentSize.width,
                      position: 'relative',
                      fontFamily: "'Segoe UI', Arial, sans-serif"
                    }}
                  >
                    {labelSize === 'small' ? (
                      <>
                        {/* Small Label with Absolute Positioning */}
                        <div style={{
                          position: 'absolute',
                          left: '5.84mm',
                          top: currentSize.headerTop,
                          width: '76.87mm',
                          height: currentSize.headerHeight,
                          textAlign: 'center',
                          fontSize: currentSize.headerSize,
                          fontWeight: 'bold',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {companyName} / Lieferschein {deliveryNoteNumber}
                        </div>
                        
                        <div style={{ position: 'absolute', left: '7.16mm', top: currentSize.kundeTop, fontSize: currentSize.labelSize, fontWeight: 'bold' }}>
                          Kunde:
                        </div>
                        <div style={{ position: 'absolute', left: '7.16mm', top: currentSize.kundeValueTop, fontSize: currentSize.valueSize, width: '54.26mm' }}>
                          {customerName || 'N/A'}
                        </div>
                        
                        <div style={{ position: 'absolute', left: '7.16mm', top: currentSize.refTop, fontSize: currentSize.labelSize, fontWeight: 'bold' }}>
                          Referenz-Nr.:
                        </div>
                        <div style={{ position: 'absolute', left: '7.16mm', top: currentSize.refValueTop, fontSize: currentSize.valueSize, width: '45.52mm' }}>
                          {referenceNumber}
                        </div>
                        
                        <div style={{ position: 'absolute', left: '7.16mm', top: currentSize.projektTop, fontSize: currentSize.labelSize, fontWeight: 'bold' }}>
                          Projekt:
                        </div>
                        <div style={{ position: 'absolute', left: '7.16mm', top: currentSize.projektValueTop, fontSize: currentSize.valueSize, width: '40.77mm' }}>
                          {deliveryNote?.project_number || 'N/A'}
                        </div>
                        
                        <div style={{
                          position: 'absolute',
                          left: currentSize.qrLeft,
                          top: currentSize.qrTop,
                          width: currentSize.qrSize,
                          height: currentSize.qrSize
                        }}>
                          <QRCodeSVG 
                            value={qrData.url} 
                            size={1000}
                            level="H"
                            includeMargin={false}
                            style={{ width: '100%', height: '100%' }}
                          />
                        </div>
                        
                        <div style={{
                          position: 'absolute',
                          left: currentSize.scanTextLeft,
                          top: currentSize.scanTextTop,
                          width: '6.88mm',
                          height: '28.39mm',
                          fontSize: currentSize.scanTextSize,
                          writingMode: 'vertical-rl',
                          transform: 'rotate(180deg)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center'
                        }}>
                          Scannen für PDF Lieferschein
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Large Label - Absolute Positioning */}
                        <div style={{
                          padding: currentSize.padding,
                          position: 'relative',
                          width: '100%',
                          height: '100%'
                        }}>
                          <div style={{
                            position: 'absolute',
                            textAlign: 'center',
                            fontSize: currentSize.headerSize,
                            fontWeight: 'bold',
                            width: '100%',
                            top: currentSize.headerTop || '0mm',
                            left: '0mm'
                          }}>
                            {companyName} / Lieferschein {deliveryNoteNumber}
                          </div>
                          
                          <div style={{ position: 'absolute', left: currentSize.textLeft || '0mm', top: currentSize.kundeTop, fontSize: currentSize.labelSize, fontWeight: 'bold' }}>
                            Kunde:
                          </div>
                          <div style={{ position: 'absolute', left: currentSize.textLeft || '0mm', top: currentSize.kundeValueTop, fontSize: currentSize.valueSize }}>
                            {customerName || 'N/A'}
                          </div>
                          
                          <div style={{ position: 'absolute', left: currentSize.textLeft || '0mm', top: currentSize.refTop, fontSize: currentSize.labelSize, fontWeight: 'bold' }}>
                            Referenz-Nr.:
                          </div>
                          <div style={{ position: 'absolute', left: currentSize.textLeft || '0mm', top: currentSize.refValueTop, fontSize: currentSize.valueSize }}>
                            {referenceNumber}
                          </div>
                          
                          <div style={{ position: 'absolute', left: currentSize.textLeft || '0mm', top: currentSize.projektTop, fontSize: currentSize.labelSize, fontWeight: 'bold' }}>
                            Projekt:
                          </div>
                          <div style={{ position: 'absolute', left: currentSize.textLeft || '0mm', top: currentSize.projektValueTop, fontSize: currentSize.valueSize }}>
                            {deliveryNote?.project_number || 'N/A'}
                          </div>
                          
                          <div style={{
                            position: 'absolute',
                            left: currentSize.qrLeft,
                            top: currentSize.qrTop,
                            width: currentSize.qrSize,
                            height: currentSize.qrSize
                          }}>
                            <QRCodeSVG value={qrData.url} size={1000} level="H" includeMargin={false} style={{ width: '100%', height: '100%' }} />
                          </div>
                          
                          <div style={{
                            position: 'absolute',
                            left: currentSize.scanTextLeft,
                            top: currentSize.scanTextTop,
                            fontSize: currentSize.scanTextSize,
                            textAlign: 'center',
                            width: currentSize.qrSize
                          }}>
                            Scannen für PDF Lieferschein
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ) : null}

            {/* Actions */}
            <div className="flex gap-3">
              <Button 
                onClick={handlePrint} 
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                disabled={!qrData || !customerName || isLoading}
              >
                <Printer className="mr-2 h-4 w-4" />
                {isLoading ? 'Laden...' : 'Drucken'}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => onOpenChange(false)}
                className="flex-1"
              >
                Abbrechen
              </Button>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                💡 <strong>Tipp:</strong> Wählen Sie die richtige Größe für Ihren DYMO-Drucker und stellen Sie in den Druckeinstellungen die passende Etikettengröße ein.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Hidden Print Content */}
      <div id="label-print-content" style={{ display: 'none' }}>
        <div id="label-content">
          {labelSize === 'small' ? (
            <>
              {/* Header */}
              <div className="label-header">
                {companyName} / Lieferschein {deliveryNoteNumber}
              </div>
              
              {/* Fields with absolute positioning */}
              <div className="label-field kunde-label">
                <span className="field-label">Kunde:</span>
              </div>
              <div className="label-field kunde-value">
                <span className="field-value">{customerName || 'N/A'}</span>
              </div>
              
              <div className="label-field ref-label">
                <span className="field-label">Referenz-Nr.:</span>
              </div>
              <div className="label-field ref-value">
                <span className="field-value">{referenceNumber}</span>
              </div>
              
              <div className="label-field projekt-label">
                <span className="field-label">Projekt:</span>
              </div>
              <div className="label-field projekt-value">
                <span className="field-value">{deliveryNote?.project_number || 'N/A'}</span>
              </div>
              
              {/* QR Code */}
              <div className="qr-code">
                {qrData && (
                  <QRCodeSVG 
                    value={qrData.url} 
                    size={1000}
                    level="H"
                    includeMargin={false}
                  />
                )}
              </div>
              
              {/* Rotated Scan Text */}
              <div className="qr-scan-text">
                Scannen für PDF Lieferschein
              </div>
            </>
          ) : (
            <>
              {/* Large Label with Absolute Positioning */}
              <div className="label-header">
                {companyName} / Lieferschein {deliveryNoteNumber}
              </div>
              
              <div className="label-field kunde-label">
                <span className="field-label">Kunde:</span>
              </div>
              <div className="label-field kunde-value">
                <span className="field-value">{customerName || 'N/A'}</span>
              </div>
              
              <div className="label-field ref-label">
                <span className="field-label">Referenz-Nr.:</span>
              </div>
              <div className="label-field ref-value">
                <span className="field-value">{referenceNumber}</span>
              </div>
              
              <div className="label-field projekt-label">
                <span className="field-label">Projekt:</span>
              </div>
              <div className="label-field projekt-value">
                <span className="field-value">{deliveryNote?.project_number || 'N/A'}</span>
              </div>
              
              <div className="qr-code">
                {qrData && (
                  <QRCodeSVG 
                    value={qrData.url} 
                    size={1000}
                    level="H"
                    includeMargin={false}
                  />
                )}
              </div>
              
              <div className="qr-scan-text">
                Scannen für PDF Lieferschein
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
